import React from "react";

const UXsection = () => {
  return (
    <section className="py-24 bg-white text-slate-800">
      <div className="container mx-auto px-6 max-w-screen-xl">
        {/* --- Section Header --- */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block py-1 px-3 rounded-full bg-blue-100 text-black text-xs font-bold uppercase tracking-widest mb-4">
            Our Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
            UI/UX Design That <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
              Feels Like Magic
            </span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            We don't just make things look good; we make them work perfectly.
            Our design process ensures your web and mobile apps are intuitive,
            accessible, and delightful.
          </p>
        </div>

        {/* --- Services Grid --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Card 1: User Research */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:shadow-2xl hover:border-blue-200 transition-all duration-300 group">
            {/* Icon Container */}
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
              <svg
                className="w-7 h-7 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-blue-600 transition-colors">
              User Research
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Deep dive into understanding your audience to create user personas
              and journey maps that guide every decision.
            </p>
          </div>

          {/* Card 2: Wireframing */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:shadow-2xl hover:border-indigo-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
              <svg
                className="w-7 h-7 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-indigo-600 transition-colors">
              Wireframing
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Creating structural blueprints of the application to define layout
              and hierarchy before adding visuals.
            </p>
          </div>

          {/* Card 3: Visual Design */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:shadow-2xl hover:border-pink-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
              <svg
                className="w-7 h-7 text-pink-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-pink-600 transition-colors">
              Visual Design
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Polishing interfaces with color theory, typography, and
              iconography to create a stunning aesthetic.
            </p>
          </div>

          {/* Card 4: Interactive Prototypes */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:shadow-2xl hover:border-purple-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
              <svg
                className="w-7 h-7 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-purple-600 transition-colors">
              Prototyping
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Building high-fidelity interactive models to test flows and user
              experience before writing a single line of code.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UXsection;
